import { useState, useEffect } from "react";
import { apiFetch, fetchUserProfiles, updateUserProfiles } from "../services/api";
import { colors, fonts } from "../styles/theme";
import { Eye, Save, Loader2, X, Plus } from "lucide-react";
import { Button } from "../components/ui";

const API_BASE = import.meta.env.VITE_API_BASE || "/api";
const ROLES = ["admin", "exec", "sales", "it-admin", "unassigned"];
const INVITE_ROLES = ["admin", "exec", "sales", "it-admin"]; // no unassigned for invites

const ROLE_COLORS = {
  admin: { bg: "#dc2626", text: "#fff" },
  exec: { bg: "#2563eb", text: "#fff" },
  sales: { bg: "#16a34a", text: "#fff" },
  "it-admin": { bg: "#9333ea", text: "#fff" },
  unassigned: { bg: "#525252", text: "#d4d4d4" },
};

const MANAGEABLE_PAGES = [
  { path: "/", label: "Dashboard" },
  { path: "/contacts", label: "Contacts" },
  { path: "/review", label: "Review" },
  { path: "/companies", label: "Companies" },
  { path: "/catalog", label: "Products" },
  { path: "/client-access", label: "Client Access" },
  { path: "/invoices", label: "Invoices" },
  { path: "/pipeline", label: "Pipeline" },
  { path: "/newsletters", label: "Newsletters" },
  { path: "/events", label: "Webcasts" },
  { path: "/engagement", label: "Engagement" },
  { path: "/network", label: "Network" },
  { path: "/videos", label: "Videos" },
  { path: "/video-library", label: "Clip Library" },
  { path: "/downloads", label: "Downloads" },
  { path: "/documents", label: "Documents" },
  { path: "/enhancements", label: "Enhancements" },
  { path: "/help", label: "Help" },
];

const inputStyle = {
  width: "100%",
  padding: "9px 12px",
  fontSize: 13,
  fontFamily: fonts.body,
  border: `1px solid ${colors.border}`,
  borderRadius: 6,
  background: colors.bg,
  color: colors.text,
  outline: "none",
  boxSizing: "border-box",
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("sales");
  const [inviting, setInviting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/clerk-users`);
      if (!res.ok) {
        const text = await res.text();
        try { const j = JSON.parse(text); throw new Error(j.error || text); } catch (e) { if (e.message) throw e; throw new Error(text); }
      }
      const data = await res.json();
      setUsers(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function updateRole(userId, newRole) {
    setSaving(userId);
    try {
      await apiFetch(`${API_BASE}/clerk-users/${userId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: newRole }),
      });
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u)),
      );
    } catch (err) {
      setError(`Failed to update role: ${err.message}`);
    } finally {
      setSaving(null);
    }
  }

  async function inviteUser(e) {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    setError(null);
    setSuccess(null);
    try {
      await apiFetch(`${API_BASE}/clerk-users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setSuccess(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setInviteRole("sales");
      setShowInvite(false);
      loadUsers();
    } catch (err) {
      setError(`Failed to invite: ${err.message}`);
    } finally {
      setInviting(false);
    }
  }

  async function deleteUser(u) {
    setDeleting(u.id);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/clerk-users/${u.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const text = await res.text();
        try { const j = JSON.parse(text); throw new Error(j.error || text); } catch (e) { if (e.message) throw e; throw new Error(text); }
      }
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      setSuccess(`${u.email || "User"} has been removed.`);
    } catch (err) {
      setError(`Failed to delete user: ${err.message}`);
    } finally {
      setDeleting(null);
      setConfirmDelete(null);
    }
  }

  function formatDate(ts) {
    if (!ts) return "Never";
    return new Date(ts).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  }

  return (
    <div style={{ padding: "2rem", maxWidth: 960 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1.5rem" }}>
        <h1 style={{ margin: 0, fontSize: "1.5rem", color: colors.text }}>
          User Management
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setShowInvite((v) => !v)}
            style={{
              padding: "0.5rem 1rem",
              background: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            + Invite User
          </button>
          <button
            onClick={loadUsers}
            disabled={loading}
            style={{
              padding: "0.5rem 1rem",
              background: colors.accent || "#4E95D9",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {showInvite && (
        <form
          onSubmit={inviteUser}
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            marginBottom: "1rem",
            padding: "1rem",
            background: colors.cardBg || "#0a1628",
            borderRadius: 8,
          }}
        >
          <input
            type="email"
            placeholder="Email address"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
            style={{
              flex: 1,
              padding: "0.5rem 0.75rem",
              borderRadius: 4,
              border: "1px solid #1e2d45",
              background: "#0a1628",
              color: "#fff",
            }}
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value)}
            style={{
              padding: "0.5rem",
              borderRadius: 4,
              border: "1px solid #1e2d45",
              background: "#0a1628",
              color: "#fff",
            }}
          >
            {INVITE_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <button
            type="submit"
            disabled={inviting}
            style={{
              padding: "0.5rem 1rem",
              background: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              opacity: inviting ? 0.6 : 1,
            }}
          >
            {inviting ? "Sending..." : "Send Invite"}
          </button>
          <button
            type="button"
            onClick={() => setShowInvite(false)}
            style={{
              padding: "0.5rem 0.75rem",
              background: "transparent",
              color: colors.textSecondary,
              border: "1px solid " + (colors.border || "#1e2d45"),
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </form>
      )}

      {success && (
        <div
          style={{
            padding: "0.75rem 1rem",
            background: "#052e16",
            color: "#4ade80",
            borderRadius: 6,
            marginBottom: "1rem",
          }}
        >
          {success}
        </div>
      )}

      {error && (
        <div
          style={{
            padding: "0.75rem 1rem",
            background: "#fef2f2",
            color: "#dc2626",
            borderRadius: 6,
            marginBottom: "1rem",
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: colors.textSecondary }}>Loading users...</p>
      ) : users.length === 0 ? (
        <p style={{ color: colors.textSecondary }}>No users found.</p>
      ) : (
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            background: colors.cardBg || "#0a1628",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <thead>
            <tr
              style={{
                borderBottom: `1px solid ${colors.border || "#1e2d45"}`,
                textAlign: "left",
              }}
            >
              <th style={thStyle}>User</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Role</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Last Sign In</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                style={{
                  borderBottom: `1px solid ${colors.border || "#1e2d45"}`,
                }}
              >
                <td style={tdStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {u.imageUrl && (
                      <img
                        src={u.imageUrl}
                        alt=""
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                        }}
                      />
                    )}
                    <span style={{ color: colors.text }}>
                      {[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}
                    </span>
                  </div>
                </td>
                <td style={{ ...tdStyle, color: colors.textSecondary }}>
                  {u.email || "—"}
                </td>
                <td style={tdStyle}>
                  <select
                    value={u.role}
                    onChange={(e) => updateRole(u.id, e.target.value)}
                    disabled={saving === u.id || u.status === "invited"}
                    style={{
                      padding: "0.3rem 0.5rem",
                      borderRadius: 4,
                      border: "1px solid " + (colors.border || "#1e2d45"),
                      background: ROLE_COLORS[u.role]?.bg || "#333",
                      color: ROLE_COLORS[u.role]?.text || "#fff",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      cursor: u.status === "invited" ? "default" : "pointer",
                      opacity: saving === u.id ? 0.5 : 1,
                    }}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={tdStyle}>
                  <span
                    style={{
                      padding: "0.2rem 0.5rem",
                      borderRadius: 4,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      background: u.status === "invited" ? "#854d0e" : "#064e3b",
                      color: u.status === "invited" ? "#fde68a" : "#6ee7b7",
                    }}
                  >
                    {u.status === "invited" ? "Invited" : "Active"}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: colors.textSecondary }}>
                  {formatDate(u.createdAt)}
                </td>
                <td style={{ ...tdStyle, color: colors.textSecondary }}>
                  {formatDate(u.lastSignInAt)}
                </td>
                <td style={tdStyle}>
                  {confirmDelete === u.id ? (
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button
                        onClick={() => deleteUser(u)}
                        disabled={deleting === u.id}
                        style={{
                          padding: "0.25rem 0.5rem",
                          background: "#dc2626",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          opacity: deleting === u.id ? 0.5 : 1,
                        }}
                      >
                        {deleting === u.id ? "..." : "Confirm"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        style={{
                          padding: "0.25rem 0.5rem",
                          background: "transparent",
                          color: colors.textSecondary,
                          border: `1px solid ${colors.border || "#1e2d45"}`,
                          borderRadius: 4,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(u.id)}
                      style={{
                        padding: "0.25rem 0.5rem",
                        background: "transparent",
                        color: "#dc2626",
                        border: "1px solid #dc2626",
                        borderRadius: 4,
                        fontSize: "0.75rem",
                        cursor: "pointer",
                      }}
                    >
                      Remove
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p
        style={{
          marginTop: "1.5rem",
          color: colors.textSecondary,
          fontSize: "0.85rem",
        }}
      >
        Invite users above — they'll receive an email to create their account.
        Roles are assigned at invite time and can be changed after they sign up.
      </p>

      <UserProfilesSection />
    </div>
  );
}

function UserProfilesSection() {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    fetchUserProfiles()
      .then((data) => {
        setProfiles(data.profiles || {});
        setLastUpdated(
          data.updatedAt ? { at: data.updatedAt, by: data.updatedBy } : null,
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const addUser = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || profiles[email]) return;
    setProfiles({ ...profiles, [email]: { hiddenPages: [] } });
    setNewEmail("");
    setSuccess(null);
  };

  const removeUser = (email) => {
    const updated = { ...profiles };
    delete updated[email];
    setProfiles(updated);
    setSuccess(null);
  };

  const togglePage = (email, path) => {
    const profile = profiles[email];
    const hidden = profile.hiddenPages || [];
    const updated = hidden.includes(path)
      ? hidden.filter((p) => p !== path)
      : [...hidden, path];
    setProfiles({ ...profiles, [email]: { ...profile, hiddenPages: updated } });
    setSuccess(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);
    try {
      const result = await updateUserProfiles(profiles);
      setProfiles(result.profiles);
      setLastUpdated({ at: result.updatedAt, by: result.updatedBy });
      setSuccess("User profiles saved");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          background: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 8,
          padding: 24,
          marginTop: 32,
          textAlign: "center",
        }}
      >
        <Loader2
          size={18}
          color={colors.textSecondary}
          className="spin"
        />      </div>
    );
  }

  const emails = Object.keys(profiles);

  return (
    <div
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 8,
        padding: 24,
        marginTop: 32,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Eye size={18} color={colors.accent} />
          <h3
            style={{
              fontFamily: fonts.heading,
              fontSize: 16,
              color: colors.text,
              margin: 0,
            }}
          >
            Page Visibility
          </h3>
        </div>
      </div>

      <p
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginBottom: 16,
          lineHeight: 1.5,
        }}
      >
        Control which pages each user can see. By default every user sees all
        pages their role allows. Add a user below and uncheck pages to hide
        them. Users without a profile here keep the default view.
      </p>

      {error && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(239,68,68,0.1)",
            color: colors.danger,
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            padding: "8px 12px",
            marginBottom: 12,
            borderRadius: 6,
            background: "rgba(16,185,129,0.1)",
            color: colors.success,
            fontSize: 12,
          }}
        >
          {success}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <input
          type="email"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addUser()}
          placeholder="user@example.com"
          style={{ ...inputStyle, flex: 1 }}
        />
        <Button variant="default" onClick={addUser}>
          <Plus size={13} /> Add User
        </Button>
      </div>

      {emails.length === 0 ? (
        <div
          style={{
            padding: 20,
            textAlign: "center",
            color: colors.textSecondary,
            fontSize: 13,
            border: `1px dashed ${colors.border}`,
            borderRadius: 6,
          }}
        >
          No user profiles configured. All users see their default role-based
          pages.
        </div>
      ) : (
        emails.map((email) => {
          const hidden = profiles[email].hiddenPages || [];
          return (
            <div
              key={email}
              style={{
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                padding: 16,
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: fonts.body,
                    color: colors.text,
                  }}
                >
                  {email}
                </span>
                <X
                  size={16}
                  color={colors.danger}
                  style={{ cursor: "pointer", flexShrink: 0 }}
                  onClick={() => removeUser(email)}
                />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {MANAGEABLE_PAGES.map((page) => {
                  const isHidden = hidden.includes(page.path);
                  return (
                    <label
                      key={page.path}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontFamily: fonts.body,
                        color: isHidden ? colors.textSecondary : colors.text,
                        cursor: "pointer",
                        padding: "3px 8px",
                        borderRadius: 4,
                        border: `1px solid ${isHidden ? colors.border : colors.accent + "60"}`,
                        background: isHidden
                          ? "transparent"
                          : colors.accent + "0d",
                        transition: "all 0.15s",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => togglePage(email, page.path)}
                        style={{ accentColor: colors.accent, margin: 0 }}
                      />
                      {page.label}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 16,
        }}
      >
        {lastUpdated ? (
          <span style={{ fontSize: 11, color: colors.textSecondary }}>
            Last saved {new Date(lastUpdated.at).toLocaleString()} by{" "}
            {lastUpdated.by}
          </span>
        ) : (
          <span />
        )}
        <Button
          variant="primary"
          onClick={handleSave}
          style={{ opacity: saving ? 0.6 : 1 }}
        >
          {saving ? (
            <>
              <Loader2
                size={13}
                className="spin"
              />{" "}
              Saving...
            </>
          ) : (
            <>
              <Save size={13} /> Save Profiles
            </>
          )}
        </Button>
      </div>    </div>
  );
}

const thStyle = {
  padding: "0.75rem 1rem",
  color: "#94a3b8",
  fontSize: "0.8rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const tdStyle = {
  padding: "0.75rem 1rem",
};
